// src/components/FittingAppointment.js

import React, { useState } from "react";
import VoiceNotePlayer from "./VoiceNotePlayer";
import FittingCalendar from "./FittingCalendar";
import FittingPayment from "./FittingPayment";
import "../style/FittingAppointment.css";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const FittingAppointment = ({ starIcon, product }) => {
  const { t, i18n } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [step, setStep] = useState("calendar"); // "calendar" | "details" | "payment" | "success"
  const [appointmentData, setAppointmentData] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "+33",
  });
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    // Reset state when closing
    if (isExpanded) {
      setStep("calendar");
      setAppointmentData(null);
      setCustomerInfo({ name: "", email: "", phone: "+33" });
    }
  };

  // When user selects a slot and clicks confirm
  const handleConfirmSlot = (data) => {
    // Format the date for display using i18n language
    const dateFormatted = data.date.toLocaleDateString(i18n.language, {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    setAppointmentData({
      ...data,
      dateFormatted,
    });
    setStep("details");
  };

  // When user submits their details
  const handleSubmitDetails = (e) => {
    e.preventDefault();
    if (customerInfo.name && customerInfo.email && customerInfo.phone) {
      setStep("payment");
    }
  };

  // When payment is successful
  const handlePaymentSuccess = (appointment) => {
    setConfirmedAppointment(appointment);
    setStep("success");
  };

  // Go back from details to calendar
  const handleBackToCalendar = () => {
    setStep("calendar");
    setAppointmentData(null);
  };

  // Go back from payment to details
  const handleBackToDetails = () => {
    setStep("details");
  };

  // Close everything after success
  const handleDone = () => {
    setIsExpanded(false);
    setStep("calendar");
    setAppointmentData(null);
    setCustomerInfo({ name: "", email: "", phone: "+33" });
    setConfirmedAppointment(null);
  };

  return (
    <div className="fitting-appointment-wrapper">
      {/* Top Row - Button and Star stay together */}
      <div className="fitting-appointment-top-row">
        <div
          className={`fitting-appointment-button ${isExpanded ? "expanded" : ""}`}
          onClick={handleToggle}
        >
          {t("Fitting Appointment")}
        </div>
        {isExpanded && (
          <div className="fitting-appointment-close" onClick={handleToggle}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
        {starIcon}
      </div>

      {/* Expandable Content */}
      <div
        className={`fitting-appointment-content ${isExpanded ? "show" : ""}`}
      >
        {/* Voice Note Player - Always visible */}
        {isExpanded && (
          <VoiceNotePlayer
            autoPlay
            audioSrcEn="/voicenote_engl.mp3"
            audioSrcFr="/voicenote_fren.mp3"
          />
        )}
        {/* Step 1: Calendar */}
        {step === "calendar" && (
          <FittingCalendar onConfirm={handleConfirmSlot} />
        )}

        {/* Step 2: Customer Details Form */}
        {step === "details" && appointmentData && (
          <div className="fa-details-section">
            <div className="fa-details-header">
              <h3>{t("Your Details")}</h3>
            </div>

            <form onSubmit={handleSubmitDetails} className="fa-details-form">
              <div className="fa-form-group">
                <input
                  type="text"
                  id="customerName"
                  value={customerInfo.name}
                  onChange={(e) =>
                    setCustomerInfo({ ...customerInfo, name: e.target.value })
                  }
                  placeholder={t("Enter your full name")}
                  required
                />
              </div>

              <div className="fa-form-group">
                <input
                  type="email"
                  id="customerEmail"
                  value={customerInfo.email}
                  onChange={(e) =>
                    setCustomerInfo({ ...customerInfo, email: e.target.value })
                  }
                  placeholder={t("Enter your email")}
                  required
                />
              </div>

              <div className="fa-form-group">
                <PhoneInput
                  country={"fr"}
                  value={customerInfo.phone}
                  onChange={(phone) =>
                    setCustomerInfo({ ...customerInfo, phone })
                  }
                  inputStyle={{ width: "100%" }}
                  placeholder={t("Phone number")}
                  enableSearch={true}
                />
              </div>

              <div className="fa-button-row">
                <button
                  type="button"
                  className="fa-back-button"
                  onClick={handleBackToCalendar}
                >
                  {t("Back")}
                </button>
                <button type="submit" className="fa-continue-button">
                  {t("Continue to Payment")}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === "payment" && appointmentData && (
          <FittingPayment
            appointmentData={appointmentData}
            customerInfo={customerInfo}
            productInfo={product}
            onSuccess={handlePaymentSuccess}
            onCancel={handleBackToDetails}
          />
        )}

        {/* Step 4: Success */}
        {step === "success" && confirmedAppointment && (
          <div className="fa-success-section">
            <div className="fa-success-icon">
              <svg
                width="60"
                height="60"
                viewBox="0 0 60 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="30" cy="30" r="30" fill="#10B981" />
                <path
                  d="M20 30L27 37L40 24"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3>{t("Appointment Confirmed!")}</h3>
            <p>{t("Your fitting appointment has been booked.")}</p>

            <div className="fa-success-details">
              <p>
                <strong>{t("Date")}:</strong> {appointmentData.dateFormatted}
              </p>
              <p>
                <strong>{t("Time")}:</strong> {appointmentData.slot} (Paris
                Time)
              </p>
              <p>
                <strong>{t("Email")}:</strong> {customerInfo.email}
              </p>
            </div>

            <p className="fa-success-note">
              {t(
                "A confirmation email has been sent to your email address. The €50 deposit will be deducted from your final order.",
              )}
            </p>

            <button className="fa-done-button" onClick={handleDone}>
              {t("Done")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FittingAppointment;
